import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BruneiFlag } from 'components/icons/BruneiFlag';
import { EastTimorFlag } from 'components/icons/EastTimorFlag';
import { GuyanaFlag } from 'components/icons/GuyanaFlag';
import { SaintVincentFlag } from 'components/icons/SaintVincentFlag';
import { VaticanCityFlag } from 'components/icons/VaticanCityFlag';
import { Button } from 'components/ui/Button';
import { Checkbox } from 'components/ui/Checkbox';
import { Dialog, DialogContent } from 'components/ui/Dialog';
import { Input } from 'components/ui/Input';
import { SectionLabel } from 'components/ui/SectionLabel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/Select';
import { Toast } from 'components/ui/Toast';
import {
  BackendRoute,
  languagesToISOAbbreviationMap,
  RouterKey,
  sectorTypeToLabelMap,
} from 'consts';
import { useLanguage } from 'context/language.context';
import countries from 'dictionaries/countries';
import { StringKey } from 'dictionaries/string-key.enum';
import { cn } from 'lib/utils';
import { useCallback, useMemo, useState } from 'react';
import * as flags from 'react-flags-select';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import apiService from 'services/api.service';
import { HttpException } from 'services/http.exception';
import { IconProps, SectorType } from 'types';
import { requesDemoSchema } from 'validations';
import z from 'zod';

export const RequestDemoSection = () => {
  const { t, language, direction } = useLanguage();
  const navigate = useNavigate();

  const [isDialogOpened, setIsDialogOpened] = useState<boolean>(false);

  const form = useForm<z.infer<typeof requesDemoSchema>>({
    resolver: zodResolver(requesDemoSchema),
  });

  const requestDemo = useCallback(
    async (data: z.infer<typeof requesDemoSchema>) => {
      await apiService.post(
        {
          endpoint: BackendRoute.REQUEST_DEMO,
        },
        {
          body: {
            ...data,
            country: countries.getName(data.country, languagesToISOAbbreviationMap[language], {
              select: 'official',
            }),
          },
        },
      );
    },
    [t],
  );

  const requestDemoMutation = useMutation({
    mutationFn: requestDemo,
    onSuccess: () => {
      setIsDialogOpened(true);
      form.reset();
    },
    onError: (error) => {
      toast(
        <Toast
          type="error"
          title={t(StringKey.TOAST_ERROR_TITLE)}
          description={
            error instanceof HttpException
              ? error.body?.statusCode === 429
                ? t(StringKey.TOO_MANY_REQUESTS_MSG)
                : error.body?.message
              : t(StringKey.TOAST_ERROR_DESC)
          }
        />,
        {
          className: 'p-0 w-[calc(100%-32px)] mx-4 xs:m-0 xs:w-[400px] bg-transparent',
        },
      );
    },
  });

  const onSubmit = (data: z.infer<typeof requesDemoSchema>) => {
    requestDemoMutation.mutate({ ...data });
  };

  const {
    firstName: firstNameError,
    lastName: lastNameError,
    email: emailError,
    phoneNumber: phoneNumberError,
    organizationName: organizationNameError,
    sectorType: sectorTypeError,
    country: countryError,
    agreed: agreedError,
  } = form.formState.errors;

  const extraFlags: Record<string, React.FC<IconProps> | undefined> = {
    BN: BruneiFlag,
    TL: EastTimorFlag,
    GY: GuyanaFlag,
    VC: SaintVincentFlag,
    VA: VaticanCityFlag,
  };

  const sortedCountries = useMemo(
    () => [
      ...Object.entries(
        countries.getNames(languagesToISOAbbreviationMap[language], {
          select: 'official',
        }),
      ).sort((prev, cur) => (prev[1].replace('Å', 'A') < cur[1].replace('Å', 'A') ? -1 : 1)),
    ],
    [language],
  );

  const countryWatch = form.watch('country');

  return (
    <section id="product" className="relative w-full overflow-x-hidden bg-[#0B0910]">
      <Dialog onOpenChange={setIsDialogOpened} open={isDialogOpened}>
        <DialogContent className="p-0 gap-0 w-[calc(100%-32px)] sm:w-[calc(100%-64px)] md:w-1/2">
          <div className="flex flex-col items-center p-5 md:p-8 gap-6">
            <h3 className="bg-clip-text bg-label-gradient font-semibold text-[30px] leading-[38px] md:text-[36px] md:leading-[44px] text-transparent text-center">
              {t(StringKey.REQUEST_SUCCESS_TITLE)}
            </h3>
            <p className="text-m leading-6 md:text-xl md:leading-[30px] text-center">
              {t(StringKey.REQUEST_SUCCESS_DESC)}
            </p>
          </div>
          <img
            className="w-full h-6 block object-cover"
            src="/assets/form-divider.png"
            alt="form-divider"
          />
          <div className="p-5 md:p-8">
            <Button className="w-full" onClick={() => setIsDialogOpened(false)}>
              {t(StringKey.GOT_IT)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <img className="absolute hidden md:block" src="/assets/product-bg.png" alt="product-bg" />
      <img
        className="absolute block md:hidden"
        src="/assets/product-bg-mobile.png"
        alt="product-bg-mobile"
      />
      <div className="relative z-2 flex flex-col items-center xl:items-start xl:flex-row gap-8 px-4 sm:px-8 md:px-28 py-16 md:py-24">
        <div className="relative flex flex-col items-center xl:items-start xl:w-[45%]">
          <SectionLabel>{t(StringKey.MAAZEEB_IN_ACTION)}</SectionLabel>
          <h1 className="bg-clip-text bg-label-gradient mt-3 text-transparent font-semibold text-[40px] leading-[48px] md:text-[60px] md:leading-[72px] text-center xl:text-start">
            {t(StringKey.PERSONALIZED_DEMO_TITLE)}
          </h1>
          <p className="mt-4 text-center xl:text-start text-sm md:text-m">
            {t(StringKey.DEMO_TOOLS_DESC)}
          </p>
        </div>
        <div className="relative xl:w-[calc(55%-32px)]">
          <form
            className="bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)] rounded-3xl border border-[#333333]"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-4 p-8">
              <h3 className="text-xl font-semibold">{t(StringKey.SEE_DIFFERENCE)}</h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <Input
                  label={t(StringKey.FIRST_NAME)}
                  placeholder={t(StringKey.ENTER_FIRST_NAME)}
                  {...form.register('firstName')}
                  errorMessage={firstNameError?.message}
                />
                <Input
                  label={t(StringKey.LAST_NAME)}
                  placeholder={t(StringKey.ENTER_LAST_NAME)}
                  {...form.register('lastName')}
                  errorMessage={lastNameError?.message}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Input
                  dir="ltr"
                  label={t(StringKey.EMAIL)}
                  placeholder={t(StringKey.ENTER_EMAIL)}
                  {...form.register('email')}
                  errorMessage={emailError?.message}
                  className={cn(direction === 'rtl' ? 'text-right' : 'text-left')}
                />
                <Input
                  dir="ltr"
                  label={t(StringKey.PHONE_NUMBER)}
                  placeholder={t(StringKey.ENTER_PHONE_NUMBER)}
                  {...form.register('phoneNumber')}
                  errorMessage={phoneNumberError?.message}
                  className={cn(direction === 'rtl' ? 'text-right' : 'text-left')}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Input
                  label={t(StringKey.ORGANIZATION_NAME)}
                  placeholder={t(StringKey.ENTER_ORGANIZATION_NAME)}
                  {...form.register('organizationName')}
                  errorMessage={organizationNameError?.message}
                />
                <Select
                  label={t(StringKey.SECTOR_TYPE)}
                  errorMessage={sectorTypeError?.message}
                  onValueChange={(value) => {
                    form.setValue('sectorType', value as SectorType);
                    form.trigger('sectorType');
                  }}
                  value={t(sectorTypeToLabelMap[form.watch('sectorType') as SectorType]) || null}
                  className="flex-1 min-w-56"
                >
                  <SelectTrigger error={!!sectorTypeError?.message}>
                    <SelectValue placeholder={t(StringKey.SELECT_YOUR_SECTOR)} />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {Object.values(SectorType).map((value, index) => (
                      <SelectItem key={index} value={value}>
                        {t(sectorTypeToLabelMap[value])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select
                  label={t(StringKey.COUNTRY)}
                  errorMessage={countryError?.message}
                  onValueChange={(value) => {
                    form.setValue('country', value);
                    form.trigger('country');
                  }}
                  value={countryWatch}
                  className="flex-1 min-w-56"
                >
                  <SelectTrigger error={!!countryError?.message}>
                    {!countryWatch ? (
                      <span className="text-gray-800">{t(StringKey.SELECT_COUNTRY)}</span>
                    ) : (
                      <span className="flex items-center justify-start gap-2">
                        <span className="min-w-5">
                          {flags[
                            `${countryWatch[0] + countryWatch.slice(1).toLowerCase()}` as 'Ae'
                          ]?.({
                            width: 20,
                            height: 20,
                          }) || extraFlags[countryWatch]?.({ width: 20, height: 20 })}
                        </span>
                        <p className="text-start line-clamp-1 break-words">
                          {countries.getName(
                            countryWatch,
                            languagesToISOAbbreviationMap[language],
                            { select: 'official' },
                          )}
                        </p>
                      </span>
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {sortedCountries.map(
                      ([code, name], index) =>
                        (!!flags[`${code[0] + code.slice(1).toLowerCase()}` as 'Ae'] ||
                          !!extraFlags[code]) && (
                          <SelectItem key={index} value={code} className="flex items-center gap-2">
                            {flags[`${code[0] + code.slice(1).toLowerCase()}` as 'Ae']?.({
                              width: 20,
                              height: 20,
                            }) || extraFlags[code]?.({ width: 20, height: 20 })}
                            <p>{name}</p>
                          </SelectItem>
                        ),
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <img
              className="w-full h-6 block object-cover"
              src="/assets/form-divider.png"
              alt="form-divider"
            />
            <div className="flex p-8 items-center justify-between max-md:gap-4 max-md:flex-col max-md:items-stretch">
              <div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!form.watch('agreed')}
                    onCheckedChange={(checked: boolean) => {
                      form.setValue('agreed', checked);
                      if (!form.formState.isSubmitted) form.trigger('agreed');
                    }}
                  />
                  <div className="text-[#969696] text-sm lg:text-m">
                    {t(StringKey.AGREE_TERMS_POLICY)}{' '}
                    <span
                      className="cursor-pointer underline"
                      onClick={() => navigate({ to: RouterKey.TERMS_OF_USE })}
                    >
                      {t(StringKey.TERMS_OF_USE)}
                    </span>
                    {' ' + t(StringKey.AND) + ' '}
                    <span
                      className="cursor-pointer underline"
                      onClick={() => navigate({ to: RouterKey.PRIVACY_POLICY })}
                    >
                      {t(StringKey.PRIVACY_POLICY)}
                    </span>
                  </div>
                </div>
                {agreedError?.message && (
                  <p className="w-0 h-4 md:h-0 min-w-[calc(100%-32px)] text-error text-sm font-light mt-1 mx-4">
                    {t(agreedError.message as StringKey) ?? agreedError.message}
                  </p>
                )}
              </div>
              <Button type="submit">{t(StringKey.REQUEST_A_DEMO)}</Button>
            </div>
          </form>
        </div>
      </div>
      <img
        className={cn(
          'absolute left-0 z-0 -mt-[360px] h-[400px] w-[320px] opacity-60 aspect-square rotate-90 object-cover object-left',
        )}
        src="/assets/laptop-bg.png"
        alt="bg-pattern-bottom-corner"
      />
      <img
        className={cn(
          'absolute right-0 z-0 -mt-[360px] h-[400px] w-[320px] opacity-60 aspect-square rotate-90 scale-y-[-1] object-cover object-left',
        )}
        src="/assets/laptop-bg.png"
        alt="bg-pattern-bottom-corner"
      />
    </section>
  );
};
